import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { InternalUsersInviteBreadcrumbs } from "../../configs/breadcrumbNavigation";
import UserForm from "./UserForm";
import Modal from "../../components/Core/Modal/Modal";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { useModalStore } from "../../hooks/modal.hooks";
import { motion } from "framer-motion";
import Flex from "../../components/Layout/Flex";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import OTPInput from "react-otp-input";
import { enable2FARequest, verify2FARequest } from "../../models/InternalUser";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import AppIcon from "../../components/Core/AppIcon/AppIcon";
import { useEnable2FA, useVerify2FA } from "../../networking/internal-user.hooks";
import { t } from "i18next";
import { useNavigate } from "react-router";
import { routes } from "../../configs/constants/routes";
import { toast } from "../../utils/toast";

const UserInvitePage: React.FC = () => {
	const { mutateAsync: enable2FA, isLoading: isEnabling2FA } = useEnable2FA();
	const { mutateAsync: verify2FA, isLoading: isVerifying2FA } = useVerify2FA();
	const [step, setStep] = useState<number>(-1);
	const [otpEnabled, setOtpEnabled] = useState<boolean>(false);
	const [phoneNumber, setPhoneNumber] = useState("");
	const [timer, setTimer] = useState(60); // 60 seconds countdown
	const [isDisabled, setIsDisabled] = useState(false);
	const [verificationId, setVerificationId] = useState("");
	const [userId, setUserId] = useState("");
	const [otp, setOtp] = useState("");
	const [otpError, setOtpError] = useState<boolean>(false);
	const [otpErrorMessage, setOtpErrorMessage] = useState<string>("");
	const toggleModal = useModalStore((state) => state.toggleMessageModal);
	const navigate = useNavigate();

	function verificationIdHandler(verificationId: string) {
		setVerificationId(verificationId);
		if (verificationId) {
			setIsDisabled(true);
		}
	}

	const handleSubmit = async () => {
		if (step !== 2) {
			setStep(step + 1);
		} else {
			setStep(-1);
		}
	};

	function closeModal() {
		toggleModal();
		setStep(-1);
		setOtp("");
		setOtpError(false);
		setOtpErrorMessage("");
		setPhoneNumber("");
		setTimer(60);
		setVerificationId("");
	}

	async function validate2FA() {
		if (otp && verificationId) {
			setOtpError(false);
			const verify2FARequest: verify2FARequest = {
				code: otp,
				verificationId: verificationId,
			};
			const otpVerifyResult = await verify2FA(verify2FARequest)
				.then(() => true)
				.catch((e) => e);

			if (otpVerifyResult === true) {
				handleSubmit();
				toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
				navigate(routes.INTERNAL_USERS);
			} else {
				setOtpError(true);
				setOtpErrorMessage(t("otp2FA.otpErrorMessage"));
			}
		} else {
			if (!otp) {
				setOtpError(true);
				setOtpErrorMessage(t("otp2FA.otpEmptyErrorMessage"));
			}
			toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	}
	async function reSendOtpSms() {
		if (phoneNumber) {
			const enable2FARequest: enable2FARequest = {
				phone: phoneNumber,
				user: {
					id: userId,
				},
			};
			const otpResult: any = await enable2FA(enable2FARequest).catch((e) => e);
			if (otpResult.verificationId) {
				setVerificationId(otpResult.verificationId);
				if (verificationId) {
					setTimer(otpResult?.ttl || 60);
					setIsDisabled(true);
				}
			} else {
				toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
			}
		}
	}

	useEffect(() => {
		let interval: any;
		if (isDisabled) {
			interval = setInterval(() => {
				setTimer((prev) => {
					if (prev === 1) {
						clearInterval(interval);
						setIsDisabled(false);
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => clearInterval(interval);
	}, [isDisabled]);

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={InternalUsersInviteBreadcrumbs} />
			<UserForm
				nextStep={() => handleSubmit()}
				setOtpEnabled={setOtpEnabled}
				setVerificationId={verificationIdHandler}
				setPhoneNumber={setPhoneNumber}
				otpEnabled={otpEnabled}
				setTimer={setTimer}
				setUserId={setUserId}
			/>
			{otpEnabled && step > -1 && (
				<Modal onClose={closeModal}>
					{(isEnabling2FA || isVerifying2FA) && <LoadingIndicator showText={false} />}
					{step === 0 && (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.5 }}
						>
							<Flex direction="column">
								<Flex
									justifyContent="center"
									alignItems="center"
									direction="row"
									margin="0 0 1rem 0"
								>
									<AppText size="l" weight="medium" margin="0 0 1rem 0" textAlign="center">
										{t("otp2FA.enable2FA")}
									</AppText>
								</Flex>

								<AppText size="m" weight="light" margin="0 0 1rem 0" textAlign="center">
									{t("otp2FA.enable2FAMessage")}
								</AppText>

								<Flex
									direction="column"
									alignItems="center"
									justifyContent="center"
									margin="1.5rem 0 0 0"
									gap="0.75rem"
								>
									<Button onClick={handleSubmit} width="80%">
										{t("otp2FA.sendCode")}
									</Button>
									<Button
										width="80%"
										loading={false}
										onClick={() => closeModal()}
										textColor="black"
										backgroundColor="buttonGray"
									>
										{t("otp2FA.cancel")}
									</Button>
								</Flex>
							</Flex>
						</motion.div>
					)}
					{step === 1 && (
						<motion.div
							initial={{ opacity: 0, x: 100 }} // Start from right (100px)
							animate={{ opacity: 1, x: 0 }} // Move to original position
							exit={{ opacity: 0, x: -100 }} // Exit to the left (-100px)
							transition={{ duration: 0.5, ease: "easeOut" }}
						>
							<Flex direction="column">
								<Flex
									justifyContent="center"
									alignItems="center"
									direction="row"
									margin="0 0 1rem 0"
								>
									<AppText size="l" weight="medium" margin="0 0 1rem 0" textAlign="center">
										{t("otp2FA.enterCode")}
									</AppText>
								</Flex>
								<AppText size="m" weight="light" margin="0 0 1rem 0" textAlign="center">
									{t("otp2FA.enable2FAMessage")}
								</AppText>
								<Flex direction="row" justifyContent="center" alignItems="center">
									<OTPInput
										value={otp}
										onChange={setOtp}
										numInputs={6}
										renderSeparator={<span> </span>}
										renderInput={(props) => <input {...props} />}
										inputStyle={{
											width: "50px",
											height: "50px",
											margin: "0 5px",
											fontSize: "20px",
											borderRadius: "4px",
											border: "1px solid gray",
											textAlign: "center",
											outline: "none",
										}}
									/>
								</Flex>
								{otpError && (
									<Flex
										direction="row"
										justifyContent="center"
										alignItems="center"
										margin="1.5rem 0 0 0"
									>
										<p style={{ fontSize: "12px", color: "red" }}>{otpErrorMessage}</p>
									</Flex>
								)}

								<Flex
									direction="column"
									alignItems="center"
									justifyContent="center"
									margin="1.5rem 0 0 0"
									gap="0.75rem"
								>
									<Button onClick={() => validate2FA()} width="80%">
										{t("otp2FA.confirm")}
									</Button>
									<Button
										width="80%"
										loading={false}
										onClick={() => closeModal()}
										textColor="black"
										backgroundColor="buttonGray"
									>
										{t("otp2FA.cancel")}
									</Button>
								</Flex>
								<Flex
									direction="row"
									justifyContent="center"
									alignItems="center"
									margin="1.5rem 0 0 0"
								>
									{isDisabled ? (
										<p style={{ fontSize: "12px", color: "#000" }}>
											{t("otp2FA.resendCodeMessage")}
											{` `} {timer} {` `} {t("otp2FA.second")}
										</p>
									) : (
										<p style={{ fontSize: "12px", color: "#000" }}>
											{t("otp2FA.resendCodeRemaining")}{" "}
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													reSendOtpSms();
												}}
												style={{
													color: "#3498db",
													textDecoration: "underline",
													cursor: "pointer",
												}}
											>
												{t("otp2FA.sendNewCode")}
											</a>
										</p>
									)}
								</Flex>
							</Flex>
						</motion.div>
					)}
					{step === 2 && (
						<motion.div
							initial={{ opacity: 0, x: 100 }} // Start from right (100px)
							animate={{ opacity: 1, x: 0 }} // Move to original position
							exit={{ opacity: 0, x: -100 }} // Exit to the left (-100px)
							transition={{ duration: 0.5, ease: "easeOut" }}
						>
							<Flex direction="column">
								<Flex
									justifyContent="center"
									alignItems="center"
									direction="row"
									margin="0 0 1rem 0"
								>
									<AppIcon icon="otp_check" size="75px" />
								</Flex>

								<Flex
									justifyContent="center"
									alignItems="center"
									direction="row"
									margin="0 0 1rem 0"
								>
									<AppText size="l" weight="medium" margin="0 0 1rem 0" textAlign="center">
										{t("otp2FA.enabled2FA")}
									</AppText>
								</Flex>

								<AppText size="m" weight="light" margin="0 0 1rem 0" textAlign="center">
									{t("otp2FA.enabled2FAMessage")}
								</AppText>

								<Flex
									direction="column"
									alignItems="center"
									justifyContent="center"
									margin="1.5rem 0 0 0"
									gap="0.75rem"
								>
									<Button onClick={() => closeModal()} width="80%">
										{t("otp2FA.close")}
									</Button>
								</Flex>
							</Flex>
						</motion.div>
					)}
				</Modal>
			)}
		</div>
	);
};

export default UserInvitePage;
